import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { PermissionProvider } from './contexts/PermissionContext';
import ToastContainer from './components/common/Toast';

// Auth pages
import Login from './pages/auth/Login';
import ForgotPassword from './pages/auth/ForgotPassword';

// Leave Request pages
import MyLeaveRequests from './pages/leave-requests/MyLeaveRequests';
import LeaveRequestForm from './components/leave-requests/LeaveRequestForm';
import LeaveApprovals from './pages/leave-requests/LeaveApprovals';
import LeaveBalanceDashboard from './pages/leave-requests/LeaveBalanceDashboard';
import LeaveRequestDetail from './pages/leave-requests/LeaveRequestDetail';
import EditLeaveRequest from './pages/leave-requests/EditLeaveRequest';

// Reports pages
import ReportsNavigation from './pages/reports/ReportsNavigation';
import HeadcountReport from './pages/reports/HeadcountReport';
import LeaveSummaryReport from './pages/reports/LeaveSummaryReport';
import LateSummaryReport from './pages/reports/LateSummaryReport';
import ApprovalSlaReport from './pages/reports/ApprovalSlaReport';

// Settings pages
import SystemSettings from './pages/settings/SystemSettings';
import AuditLogs from './pages/settings/AuditLogs';

// Employee & User management pages
import EmployeeList from './pages/employees/EmployeeList';
import UserList from './pages/users/UserList';

// Department management
import DepartmentList from './pages/departments/DepartmentList';

// Permission Groups
import PermissionGroupList from './pages/permission-groups/PermissionGroupList';

// Layout
import Layout from './components/layout/Layout';

// Protected Route
import ProtectedRoute from './components/common/ProtectedRoute';
import PermissionProtectedRoute from './components/common/PermissionProtectedRoute';
import { PERMISSIONS } from './contexts/PermissionContext';

function App() {
  return (
    <AuthProvider>
      <ToastProvider>
      <PermissionProvider>
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />

          {/* Protected routes */}
          <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            {/* Dashboard */}
            <Route path="/" element={<Navigate to="/leave-requests/balance" replace />} />

            {/* Leave Requests - basic pages available to all authenticated users */}
            <Route path="/leave-requests/me" element={<MyLeaveRequests />} />
            <Route path="/leave-requests/new" element={<LeaveRequestForm />} />
            <Route path="/leave-requests/balance" element={<LeaveBalanceDashboard />} />
            <Route path="/leave-requests/:id" element={<LeaveRequestDetail />} />
            <Route path="/leave-requests/:id/edit" element={<EditLeaveRequest />} />
            
            {/* Leave Approvals - requires approve permission */}
            <Route
              path="/leave-requests/approvals"
              element={
                <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.LEAVE_REQUESTS_APPROVE]}>
                  <LeaveApprovals />
                </PermissionProtectedRoute>
              }
            />

            {/* Reports - requires at least one report permission */}
            <Route
              path="/reports"
              element={
                <PermissionProtectedRoute
                  requiredPermissions={[
                    PERMISSIONS.REPORTS_HEADCOUNT,
                    PERMISSIONS.REPORTS_LEAVE_SUMMARY,
                    PERMISSIONS.REPORTS_LATE_SUMMARY,
                    PERMISSIONS.REPORTS_APPROVAL_SLA,
                  ]}
                >
                  <ReportsNavigation />
                </PermissionProtectedRoute>
              }
            />
            <Route
              path="/reports/headcount"
              element={
                <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.REPORTS_HEADCOUNT]}>
                  <HeadcountReport />
                </PermissionProtectedRoute>
              }
            />
            <Route
              path="/reports/leave-summary"
              element={
                <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.REPORTS_LEAVE_SUMMARY]}>
                  <LeaveSummaryReport />
                </PermissionProtectedRoute>
              }
            />
            <Route
              path="/reports/late-summary"
              element={
                <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.REPORTS_LATE_SUMMARY]}>
                  <LateSummaryReport />
                </PermissionProtectedRoute>
              }
            />
            <Route
              path="/reports/approval-sla"
              element={
                <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.REPORTS_APPROVAL_SLA]}>
                  <ApprovalSlaReport />
                </PermissionProtectedRoute>
              }
            />

            {/* Settings - requires settings.view permission */}
            <Route
              path="/settings"
              element={
                <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.SETTINGS_VIEW]}>
                  <SystemSettings />
                </PermissionProtectedRoute>
              }
            />
            <Route
              path="/settings/audit-logs"
              element={
                <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.AUDIT_VIEW]}>
                  <AuditLogs />
                </PermissionProtectedRoute>
              }
            />

            {/* Department Management - requires departments.view permission */}
            <Route
              path="/departments"
              element={
                <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.DEPARTMENTS_VIEW]}>
                  <DepartmentList />
                </PermissionProtectedRoute>
              }
            />

            {/* Employee Management - requires employees.view permission */}
            <Route
              path="/employees"
              element={
                <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.EMPLOYEES_VIEW]}>
                  <EmployeeList />
                </PermissionProtectedRoute>
              }
            />

            {/* User Management - requires users.view permission */}
            <Route
              path="/users"
              element={
                <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.USERS_VIEW]}>
                  <UserList />
                </PermissionProtectedRoute>
              }
            />

            {/* Permission Groups - requires groups.view permission */}
            <Route
              path="/permission-groups"
              element={
                <PermissionProtectedRoute requiredPermissions={[PERMISSIONS.GROUPS_VIEW]}>
                  <PermissionGroupList />
                </PermissionProtectedRoute>
              }
            />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <ToastContainer />
      </PermissionProvider>
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;